import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
import studentService from '../../services/studentService';
import { useStudentProgress } from '../../hooks/useStudentProgress';
import { isToday, formatDate } from '../../utils/dateUtils';
import '../../styles/students/StudentProfilePage.css';
import '../../styles/students/StudentAssignmentCardFix.css';

const StudentProfilePage = () => {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  
  // Get studentId from either params or location state
  const studentId = id || (location.state && location.state.studentId);
  const studentName = location.state && location.state.studentName;
  
  // Use the student progress hook to get all the progress metrics
  const { 
    dashboardStats,
    loading: progressLoading
  } = useStudentProgress(studentId);

  useEffect(() => {
    if (!studentId) {
      console.error('No student ID provided');
      navigate('/classes');
      return;
    }

    console.log('Fetching data for student ID:', studentId);

    const fetchStudentData = async () => {
      try {
        setLoading(true);
        
        // Initialize student object with basic structure
        let studentObj = {
          _id: studentId,
          name: studentName || 'Loading...',
          email: '',
          pendingAssessments: [],
          notes: ''
        };
        
        try {
          // First try to get basic student data
          console.log('Fetching basic student data for ID:', studentId);
          const studentData = await studentService.getStudentProfile(studentId);
          console.log('Student data received:', studentData);
          
          // Update student object with retrieved data
          studentObj = {
            _id: studentData._id || studentId,
            name: studentData.name || 'Error Loading Name',
            email: studentData.email || '',
            pendingAssessments: [],
            notes: studentData.notes || ''
          };
          
          // Finally try to get assignments data
          try {
            console.log('Fetching assignments for student ID:', studentId);
            const assignmentsData = await studentService.getStudentAssignments(studentId);
            console.log('Assignments data received:', assignmentsData);
            
            if (assignmentsData && assignmentsData.length > 0) {
              // Filter for pending assignments due today
              const pendingAssignments = assignmentsData
                .filter(assignment => 
                  // Filter by: not assessed yet AND due date is today
                  (!assignment.assessment || !assignment.assessment.rating) && 
                  isToday(assignment.dueDate)
                )
                .map(assignment => {
                  // Get the content from title or construct from surah
                  const title = assignment.title || 
                    (assignment.surah ? `${assignment.surah.name} (${assignment.verses.from}-${assignment.verses.to})` : 'Unknown Content');
                  
                  // Priority color based on due date - orange (today) by default
                  // Using the same color system as TeacherDashboard
                  let indicatorColor = '#ff9800'; // Orange for due today
                  
                  // Format the date for display
                  const dueDate = assignment.dueDate
                    ? formatDate(assignment.dueDate)
                    : 'Today';
                  
                  return {
                    _id: assignment._id || `temp-${Math.random()}`,
                    id: assignment._id,
                    title: title,
                    dueDate: dueDate,
                    type: assignment.type || 'revision',
                    indicatorColor
                  };
                });
              
              studentObj.pendingAssessments = pendingAssignments;
              console.log(`Found ${pendingAssignments.length} pending assessments for today`);
            }
          } catch (assignmentsError) {
            console.error('Error fetching assignments for student:', assignmentsError);
            // Continue with empty assignments
          }
          
        } catch (studentDataError) {
          console.error('Error fetching student data:', studentDataError);
        }
        
        // Set the final student data
        console.log('Setting final student data:', studentObj);
        setStudent(studentObj);
        setNotes(studentObj.notes || '');
        setLoading(false);
      } catch (error) {
        console.error('Fatal error in fetchStudentData:', error);
        setLoading(false);
      }
    };

    fetchStudentData();
  }, [studentId, studentName, navigate]);

  const handleSaveNotes = async (text) => {
    setNotes(text);
    setSavingNotes(true);
    
    try {
      // Try to save notes through API
      await studentService.updateStudentNotes(studentId, text);
      console.log('Notes saved successfully');
    } catch (error) {
      console.error('Error saving notes:', error);
      // Continue execution even if API fails - we've already updated the UI
    } finally {
      setSavingNotes(false);
    }
  };

  const handleAssignWork = () => {
    navigate('/assignments/new', {
      state: {
        studentId: student._id || studentId,
        studentName: student.name
      }
    });
  };

  const handleContactParent = () => {
    navigate('/messages/new', {
      state: {
        recipientType: 'parent',
        studentId: student._id || studentId,
        studentName: student.name
      }
    });
  };

  if (loading || progressLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading student profile...</p>
      </div>
    );
  }

  return (
    <div className="student-profile-page">
      <div className="page-header">
        <h1>Student Profile</h1>
        <button 
          onClick={() => navigate(-1)} 
          className="back-button"
        >
          ← Back
        </button>
      </div>

      {/* Student Profile Card */}
      <div className="profile-card">
        <div className="profile-avatar">
          {student.name?.charAt(0) || '?'}
        </div>
        <div className="profile-info">
          <h2 className="profile-name">{student.name}</h2>
          {student.email && <p className="profile-email">{student.email}</p>}
        </div>
      </div>

      {/* Progress Summary Section */}
      <h2 className="section-title">Progress Summary</h2>
      <div className="stats-container" style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '20px 10px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        margin: '0 0 20px 0',
        overflow: 'hidden'
      }}>
        <div className="stat-item" style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
          <div className="stat-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#4a90e2', marginBottom: '6px' }}>{dashboardStats.assignmentCompletion}%</div>
          <div className="stat-label" style={{ fontSize: '14px', color: '#666' }}>Assignments</div>
          <div style={{ position: 'absolute', top: '50%', right: 0, transform: 'translateY(-50%)', width: '1px', height: '60%', backgroundColor: '#e0e0e0' }}></div>
        </div>
        
        <div className="stat-item" style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
          <div className="stat-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#4a90e2', marginBottom: '6px' }}>{dashboardStats.assessmentAverage.value}/5</div>
          <div className="stat-label" style={{ fontSize: '14px', color: '#666' }}>Avg. Rating</div>
          <div style={{ position: 'absolute', top: '50%', right: 0, transform: 'translateY(-50%)', width: '1px', height: '60%', backgroundColor: '#e0e0e0' }}></div>
        </div>
        
        <div className="stat-item" style={{ flex: 1, textAlign: 'center' }}>
          <div className="stat-value" style={{ fontSize: '28px', fontWeight: 'bold', color: '#4a90e2', marginBottom: '6px' }}>{dashboardStats.attendanceRate}%</div>
          <div className="stat-label" style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>Attendance</div>
        </div>
      </div>

      {/* Pending Assessments Section */}
      <div className="section-header">
        <h2 className="section-title">Pending Assessments For Today</h2>
      </div>
      
      <div className="assignments-list">
        {student.pendingAssessments && student.pendingAssessments.length > 0 ? (
          student.pendingAssessments.map((assignment) => (
            <div key={assignment._id || assignment.id} className="assignment-card">
              <div 
                className="assignment-indicator" 
                style={{ backgroundColor: assignment.indicatorColor }}
              ></div>
              
              <div className="assignment-content">
                <div className="assignment-header">
                  <h3 className="assignment-title">{assignment.title}</h3>
                  <div className="assignment-type-badge">
                    {assignment.title.includes('(1-') ? 'New memorization' : 'Revision'}
                  </div>
                </div>
                
                <div className="assignment-details">
                  <p className="assignment-date">
                    Due: {assignment.dueDate}
                  </p>
                </div>

                <div className="assessment-actions">
                  <button 
                    className="assess-btn"
                    onClick={() => navigate(`/assignments/${assignment.id}/assess`, {
                      state: {
                        studentId: student._id || studentId,
                        assignmentId: assignment.id,
                        studentName: student.name,
                        assignmentDetails: assignment.title
                      }
                    })}
                  >
                    Assess
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="empty-assignments">
            <p>No pending assessments for today.</p>
          </div>
        )}
      </div>

      {/* Performance Links Section */}
      <h2 className="section-title">Performance</h2>
      <div className="performance-container">
        <Link 
          to={`/assignments/student/${student._id || studentId}`}
          className="performance-link"
          state={{ studentId: student._id || studentId, studentName: student.name }}
        >
          <h3 className="performance-link-title">Assignments</h3>
          <p className="performance-link-subtitle">View all assignments</p>
        </Link>
        
        <div className="performance-divider"></div>
        
        <Link 
          to={`/students/${student._id || studentId}/attendance`}
          className="performance-link"
          state={{ studentId: student._id || studentId, studentName: student.name }}
        >
          <h3 className="performance-link-title">Attendance</h3>
          <p className="performance-link-subtitle">View attendance history</p>
        </Link>
      </div>

      {/* Actions Section */}
      <h2 className="section-title">Actions</h2>
      <div className="actions-container">
        <button 
          className="action-button"
          onClick={handleAssignWork}
        >
          New Assignment
        </button>
        
        <button 
          className="action-button"
          onClick={handleContactParent}
        >
          Contact Parent
        </button>
      </div>

      {/* Notes Section */}
      <h2 className="section-title">Notes</h2>
      <div className="notes-container">
        <textarea
          className="notes-input"
          placeholder="Add notes about this student..."
          value={notes}
          onChange={(e) => handleSaveNotes(e.target.value)}
        ></textarea>
        {savingNotes && (
          <div className="saving-indicator">
            <div className="saving-spinner"></div>
            <span>Saving...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentProfilePage;